import { ChangeEvent, ReactNode, useMemo } from 'react';

import clsx from 'clsx';
import uniq from 'lodash/uniq';

import { Checkbox, CheckboxProps } from '../../../controls/checkbox';
import { Column } from '../../Column';
import { GetRowKey, RowKey } from '../../types';
import { DataTableProps } from '../types';

type UseSelectableRows<T, K extends RowKey> = {
  getRowClass: DataTableProps<T, K>['getRowClass'];
  checkboxColumn: ReactNode;
};
export const useSelectableRows = <T, K extends RowKey>({
  onRowsSelected,
  selectedRows,
  isRowSelectable,
  rowKey,
  getRowClass,
  data,
  totalDataLength: _totalDataLength,
  checkboxColumnProps: {
    disabled: _checkboxColumnDisabled,
    getTestId,
    renderHeaderCheckbox,
    renderCellCheckbox,
    sortable,
    ...checkboxColumnProps
  } = {},
}: DataTableProps<T, K> & {
  rowKey: GetRowKey<T, K>;
}): UseSelectableRows<T, K> => {
  const selectable = !!onRowsSelected;

  const checkboxColumnDisabled = useMemo(() => {
    if (!isRowSelectable || !data?.length || !selectable) {
      return _checkboxColumnDisabled;
    }
    return !data.some((row) => isRowSelectable(row));
  }, [data, isRowSelectable, _checkboxColumnDisabled, selectable]);

  if (!selectable) {
    return { getRowClass, checkboxColumn: null };
  }

  const handleCheck = (rowKey: any, checked: boolean) => {
    if (onRowsSelected) {
      const keys = checked
        ? [...(selectedRows || []), rowKey]
        : selectedRows?.filter((id) => id !== rowKey);
      onRowsSelected(
        keys || [],
        `checkbox-${checked ? 'checked' : 'unchecked'}`
      );
    }
  };

  const totalDataLength = _totalDataLength ?? data?.length;
  let allChecked = false;
  let indeterminate = false;
  if (!!totalDataLength && selectedRows?.length === totalDataLength) {
    allChecked = true;
  } else if (!selectedRows?.length) {
    allChecked = false;
  } else if (
    selectedRows.length &&
    totalDataLength &&
    selectedRows.length < totalDataLength
  ) {
    indeterminate = true;
  }

  const handleCheckAll = (
    _: ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    if (!data || !onRowsSelected) {
      return;
    }
    if (indeterminate || (allChecked && !checked)) {
      onRowsSelected([], 'select-all-unchecked');
    } else if (checked) {
      if (isRowSelectable) {
        onRowsSelected(
          uniq([
            ...(selectedRows || []),
            ...data.filter((record) => isRowSelectable(record)).map(rowKey),
          ]),
          'select-all-checked'
        );
      } else {
        onRowsSelected(
          uniq([...(selectedRows || []), ...data.map(rowKey)]),
          'select-all-checked'
        );
      }
    }
  };
  return {
    getRowClass: (row, rowState) => {
      const selectionDisabled = isRowSelectable
        ? !isRowSelectable(row, rowState)
        : false;
      const isChecked = allChecked || selectedRows?.includes(rowState.rowKey);
      return clsx(
        getRowClass?.(row, rowState),
        selectionDisabled && 'DS-Table-rowSelectionDisabled',
        isChecked && 'DS-Table-rowSelected'
      );
    },
    checkboxColumn: (
      <Column<T, K>
        id="_checkBox"
        width={sortable ? 50 : 32}
        renderHeader={() => {
          const checkboxProps: CheckboxProps = {
            testId: 'check-all',
            checked: allChecked || indeterminate,
            indeterminate,
            onChange: handleCheckAll,
            onClick: (e) => e.stopPropagation(),
            size: 'small',
            disabled: checkboxColumnDisabled,
          };
          if (renderHeaderCheckbox) {
            return renderHeaderCheckbox(checkboxProps);
          }
          return <Checkbox {...checkboxProps} />;
        }}
        renderCell={(row, rowState) => {
          const checkboxProps: CheckboxProps = {
            value: rowState.rowKey,
            checked: allChecked || selectedRows?.includes(rowState.rowKey),
            onChange: (_, checked) => {
              handleCheck(rowState.rowKey, checked);
            },
            disabled:
              checkboxColumnDisabled ||
              (isRowSelectable ? !isRowSelectable(row, rowState) : false),
            onClick: (e) => e.stopPropagation(),
            size: 'small',
            testId: getTestId?.(row) ?? 'data-table-row-checkbox',
          };
          if (renderCellCheckbox) {
            return renderCellCheckbox(checkboxProps, row, rowState);
          } else {
            return <Checkbox {...checkboxProps} />;
          }
        }}
        cellWrapperSx={{ px: 8 }}
        cellSx={{ display: 'inline-flex' }}
        frozen
        excludeFromSettings
        stopClickPropagation
        accessor={
          sortable ? (row: T) => selectedRows?.includes(rowKey(row)) : undefined
        }
        sortable={sortable}
        {...checkboxColumnProps}
      />
    ),
  };
};
